<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import SettingGuide from 'src/components/DevicesSettings/SettingGuide.svelte';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	import { Container, InputV2, Text, TokenV2 } from '@Shelly/shelly-design-system';
	import {
		celsius_to_fahrenheit,
		fahrenheit_to_celsius,
		isBluTRV,
	} from 'src/helpers/interface_helpers';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import {
		BLU_TRV_MAX_COMPENSATION_C_MAX,
		BLU_TRV_MAX_COMPENSATION_C_MIN,
		BLU_TRV_MAX_TARGET_C_MAX,
		BLU_TRV_MAX_TARGET_C_MIN,
		BLU_TRV_MIN_TARGET_C_MAX,
		BLU_TRV_MIN_TARGET_C_MIN,
		bluTrvHasCompensation,
		bluTrvHasTargetLimits,
		bluTrvTargetOrderIsValid,
		buildBluTrvSetConfigSlice,
	} from 'src/helpers/settings/blu_trv_settings_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import {
		capFirstChar,
		getErrorOrInfoMessageForMinAndMax,
		getErrorOrInfoMessageForMinAndMaxUnit,
	} from 'src/helpers/utilities';
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';

	export let device: Device;

	const min = -12.5;
	const max = 12.5;
	const trvComponent = getTrvComponentByDevice(device);
	const trvConfig = trvComponent?.config?.['trv:0'];
	const savedOffset = trvComponent?.config?.['temperature:0']?.offset_C || 0;
	const savedUnit: 'C' | 'F' = device?.ht_ui?.temperature_unit === 'F' ? 'F' : 'C';
	const hasTargetLimits = bluTrvHasTargetLimits(trvConfig);
	const hasCompensation = bluTrvHasCompensation(trvConfig);

	let selectedUnit: 'C' | 'F' = savedUnit;
	let offsetValue: number = savedOffset;
	let minTarget: number | string = trvConfig?.min_target_C ?? BLU_TRV_MIN_TARGET_C_MIN;
	let maxTarget: number | string = trvConfig?.max_target_C ?? BLU_TRV_MAX_TARGET_C_MAX;
	let compensation: number | string = trvConfig?.max_compensation_C ?? BLU_TRV_MAX_COMPENSATION_C_MIN;
	let waitForResponse = false;
	let lastSavedUnit: 'C' | 'F' = savedUnit;
	let lastSavedOffset: number = savedOffset;
	let lastSavedMinTarget = Number(minTarget);
	let lastSavedMaxTarget = Number(maxTarget);
	let lastSavedCompensation = Number(compensation);

	$: numericOffset = Number(offsetValue);
	$: numericMinTarget = Number(minTarget);
	$: numericMaxTarget = Number(maxTarget);
	$: numericCompensation = Number(compensation);
	$: offsetInputData = getErrorOrInfoMessageForMinAndMax(numericOffset, min, max);
	$: minTargetInfo = getErrorOrInfoMessageForMinAndMaxUnit(
		numericMinTarget,
		BLU_TRV_MIN_TARGET_C_MIN,
		BLU_TRV_MIN_TARGET_C_MAX,
		'°C',
	);
	$: maxTargetInfo = getErrorOrInfoMessageForMinAndMaxUnit(
		numericMaxTarget,
		BLU_TRV_MAX_TARGET_C_MIN,
		BLU_TRV_MAX_TARGET_C_MAX,
		'°C',
	);
	$: compensationInfo = getErrorOrInfoMessageForMinAndMaxUnit(
		numericCompensation,
		BLU_TRV_MAX_COMPENSATION_C_MIN,
		BLU_TRV_MAX_COMPENSATION_C_MAX,
		'°C',
	);
	$: targetOrderError =
		hasTargetLimits &&
		!minTargetInfo.error &&
		!maxTargetInfo.error &&
		!bluTrvTargetOrderIsValid(numericMinTarget, numericMaxTarget)
			? $format('error_trv_min_target_above_max', {
					default: 'Minimum target must be lower than maximum target.',
				})
			: '';
	$: minTargetError = minTargetInfo.error || targetOrderError;
	$: unitsDirty = selectedUnit !== lastSavedUnit;
	$: offsetDirty = numericOffset !== lastSavedOffset;
	$: minTargetDirty = hasTargetLimits && numericMinTarget !== lastSavedMinTarget;
	$: maxTargetDirty = hasTargetLimits && numericMaxTarget !== lastSavedMaxTarget;
	$: targetDirty = minTargetDirty || maxTargetDirty;
	$: compensationDirty = hasCompensation && numericCompensation !== lastSavedCompensation;
	$: isDirty = unitsDirty || offsetDirty || targetDirty || compensationDirty;
	$: hasBlockingError =
		(offsetDirty && !!offsetInputData.error) ||
		(targetDirty && (!!minTargetError || !!maxTargetInfo.error)) ||
		(compensationDirty && !!compensationInfo.error);
	$: saveDisabled = waitForResponse || !isDirty || hasBlockingError;

	const saveUnits = async () => {
		let editDevice = {
			...device,
			ht_ui: { temperature_unit: selectedUnit },
		};

		if (isBluTRV(device?.type) && device?.temp_fav_positions) {
			editDevice = {
				...editDevice,
				temp_fav_positions: device.temp_fav_positions.map((pos) => ({
					...pos,
					value:
						selectedUnit === 'C'
							? +fahrenheit_to_celsius(pos.value).toFixed(1)
							: celsius_to_fahrenheit(pos.value),
				})),
			};
		}

		const res = await requestEditDevice(editDevice);
		if (res?.isok) {
			lastSavedUnit = selectedUnit;
		}
	};

	const saveOffset = async () => {
		const res = await callBluTRV(device, 'Temperature.SetConfig', {
			id: 0,
			config: { offset_C: numericOffset },
		});
		if (res?.isok) {
			lastSavedOffset = numericOffset;
		}
	};

	const saveTrvLimits = async () => {
		const config = buildBluTrvSetConfigSlice({
			min_target_C: { dirty: minTargetDirty, value: numericMinTarget },
			max_target_C: { dirty: maxTargetDirty, value: numericMaxTarget },
			max_compensation_C: { dirty: compensationDirty, value: numericCompensation },
		});
		if (Object.keys(config).length === 0) return;

		const res = await callBluTRV(device, 'Trv.Setconfig', {
			id: 0,
			config,
		});
		if (res?.isok) {
			if (minTargetDirty) lastSavedMinTarget = numericMinTarget;
			if (maxTargetDirty) lastSavedMaxTarget = numericMaxTarget;
			if (compensationDirty) lastSavedCompensation = numericCompensation;
		}
	};

	const save = async () => {
		if (saveDisabled) return;
		waitForResponse = true;
		if (unitsDirty) await saveUnits();
		if (offsetDirty && !offsetInputData.error) await saveOffset();
		if ((targetDirty || compensationDirty) && !hasBlockingError) await saveTrvLimits();
		waitForResponse = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading: waitForResponse,
			disabled: saveDisabled,
			icon: 'fas fa-save',
		});
	}
</script>

<div class="blu-trv-temperature-settings">
	<SettingGuide
		text={$format('settings_guide_trv_temperature', {
			default:
				'App units and sensor offset, plus the target range and algorithm compensation stored on the valve.',
		})}
	/>

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{capFirstChar(
				$format('sub_settings_title_temperature_unit', {
					default: 'Temperature units',
				}),
			)}
		</Text>
		<Text styleType="body-small" class="mb-s text-color-muted">
			{$format('select_temperature_units', {
				default: 'Choose temperature units:',
			})}
		</Text>
		<div class="d-flex">
			<TokenV2
				id="token-blu-trv-unit-c"
				selected={selectedUnit === 'C'}
				size="m"
				title="°C"
				class="mr-s"
				on:click={() => {
					selectedUnit = 'C';
				}}
			/>
			<TokenV2
				id="token-blu-trv-unit-f"
				selected={selectedUnit === 'F'}
				size="m"
				title="°F"
				on:click={() => {
					selectedUnit = 'F';
				}}
			/>
		</div>
	</Container>

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{capFirstChar(
				$format('sub_settings_title_temperature_offset', {
					default: 'temperature offset',
				}),
			)}
		</Text>
		<Text styleType="body-small" class="mb-s text-color-muted">
			{$format('text_trv_offset_description', {
				default:
					'Offset in °{units} to be applied to the measured temperature. The value must be between {min}°{units} and {max}°{units}.',
				values: { units: 'C', min, max },
			})}
		</Text>
		<InputV2
			aria-label={$format('input_temperature_offset', {
				default: 'Enter the temperature offset. Value must be between {min} — {max}!',
				values: { min, max },
			})}
			{...getNumberInputConfig(true)}
			icon={null}
			size="m"
			width="full"
			bind:value={offsetValue}
			info={offsetInputData.info}
			error={offsetInputData.error}
			id="input-blu-trv-temp-offset"
		/>
	</Container>

	{#if hasTargetLimits}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_trv_target_range', {
					default: 'Target temperature range',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_desc_trv_target_range', {
					default:
						'Limits the target temperatures you can set on the valve, in °C. Minimum must be lower than maximum.',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_heading_trv_min_target', { default: 'Minimum' })}
			</Text>
			<InputV2
				aria-label={$format('input_blu_trv_min_target', {
					default:
						'Enter the minimum target temperature in °C. Value must be between {min} — {max}!',
					values: { min: BLU_TRV_MIN_TARGET_C_MIN, max: BLU_TRV_MIN_TARGET_C_MAX },
				})}
				{...getNumberInputConfig(true)}
				icon={null}
				size="m"
				width="full"
				class="mb-s"
				bind:value={minTarget}
				info={minTargetInfo.info}
				error={minTargetError}
				id="input-blu-trv-min-target"
			/>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_heading_trv_max_target', { default: 'Maximum' })}
			</Text>
			<InputV2
				aria-label={$format('input_blu_trv_max_target', {
					default:
						'Enter the maximum target temperature in °C. Value must be between {min} — {max}!',
					values: { min: BLU_TRV_MAX_TARGET_C_MIN, max: BLU_TRV_MAX_TARGET_C_MAX },
				})}
				{...getNumberInputConfig(true)}
				icon={null}
				size="m"
				width="full"
				bind:value={maxTarget}
				info={maxTargetInfo.info}
				error={maxTargetInfo.error}
				id="input-blu-trv-max-target"
			/>
		</Container>
	{/if}

	{#if hasCompensation}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_trv_max_compensation', {
					default: 'Maximum temperature compensation',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_desc_trv_max_compensation', {
					default:
						'How many extra °C the algorithm may add to the measured temperature when estimating room temperature (0–5).',
				})}
			</Text>
			<InputV2
				aria-label={$format('input_blu_trv_max_compensation', {
					default:
						'Enter the maximum temperature compensation in °C. Value must be between {min} — {max}!',
					values: { min: BLU_TRV_MAX_COMPENSATION_C_MIN, max: BLU_TRV_MAX_COMPENSATION_C_MAX },
				})}
				{...getNumberInputConfig(true)}
				icon={null}
				size="m"
				width="full"
				bind:value={compensation}
				info={compensationInfo.info}
				error={compensationInfo.error}
				id="input-blu-trv-max-compensation"
			/>
		</Container>
	{/if}
</div>

<style>
	.blu-trv-temperature-settings {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
